import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import useApi from '../../hooks/useApi';
import IInventaireActuel from '../../models/IInventaireActuel';
import DataGrid, { Column, Lookup } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, SimpleItem, Label, FormRef } from "devextreme-react/form";
import IModificationInventaire from "../../models/IModificationInventaire";
import Guid from "../../models/Guid";
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { IFormBaseDataSourceOptions, Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import useLookUpBase from '../../components/selectbox/LookupBase';
import GestionPosteEnregistrement from '../gestion-poste-enregistrement/gestion-poste-enregistrement';
import GestionLocalisation from '../gestion-localisation/gestion-localisation';
import GestionCatalogueProduits from '../gestion-catalogue-produits/gestion-catalogue-produits';

export default function GestionModificationInventaire() {
  const inventaireDataSourceOption: IFormBaseDataSourceOptions<IModificationInventaire> = useMemo(() => ({
    path: "modificationInventaire",
    primaryKey: "idModificationInventaire",
    customReadByIdPath: "modificationInventaire/{key}/gestion",
  }), []);

  const inventaireFormDataSource = useCustomDataSource<IModificationInventaire, IModificationInventaire>(inventaireDataSourceOption);
  const { setMainForm, selectedDataRow: selectedInventaire, setSelectedDataRow, mode, onFieldDataChanged, setMode, isFieldModifier, setIsFieldModifier } = inventaireFormDataSource;
  const { editorOptions: catalogueProduitsEditorOption, PopupGestion: PopupGestioCatalogueProduits } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: 'catalogue', showClear: mode !== Mode.Lecture,
    titre: "Catalogue de produits",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idProduit", Guid.empty.toString());
    }
  });
  const { editorOptions: posteEnregistrementEditorOption, PopupGestion: PopupGestionPosteEnregistrement } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "posteenregistrement",
    showClear: mode !== Mode.Lecture,
    titre: "Gestionnaire des postes d'enregistrement",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idPosteEnregistrement", Guid.empty.toString());
    }
  });
  const { editorOptions: localisationEditorOption, PopupGestion: PopupGestionLocalisation } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "localisation",
    showClear: mode !== Mode.Lecture,
    titre: "Gestion des localisations",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idLocalisation", Guid.empty.toString());
    }
  });
  const formRef = useRef<FormRef>(null);
  const { getData: getInventaireActuel } = useApi<IInventaireActuel[]>();

  const loadInventaireActuel = useCallback(async (idProduit: Guid) => {
    if (!idProduit || idProduit.toString() === Guid.empty.toString()) {
      setSelectedDataRow((prev) => ({ ...prev, inventairesActuel: [] }));
      return;
    }

    const inventaires = await getInventaireActuel({
      customPath: `modificationInventaire/inventaireActuel/${idProduit}`,
      returnOnly: true,
    });
    setSelectedDataRow((prev) => ({ ...prev, inventairesActuel: inventaires ?? [] }));
  }, [getInventaireActuel, setSelectedDataRow]);

  const handleFieldDataChanged = useCallback((e: { dataField?: string, value?: unknown }) => {
    onFieldDataChanged(e as any);
    if (e.dataField === "idProduit" && e.value) {
      loadInventaireActuel(e.value as Guid);
    }
  }, [onFieldDataChanged, loadInventaireActuel]);

  const { explicationOptions } = useMemo(() => {
    return {
      explicationOptions: { height: 125 }
    };
  }, []);

  const quantiteModifieeEditorOptions = useMemo(() => {
    return {
      format: "#,##0.0000",
      min: 0,
      width: "50%",
      textAlign: "right",
      inputAttr: {
        class: 'dx-texteditor-input',
        style: 'text-align: right;'
      }
      // showSpinButtons: true
    };
  }, []);

  const dateDeModificationEditorOptions = useMemo(() => {
    return {
      displayFormat: "yyyy-MM-dd",
      type: "date",
      width: "50%"
    };
  }, []);

  const headerText = useMemo(() => {
    if (catalogueProduitsEditorOption?.dataSource) {
      for (let i = 0; i < catalogueProduitsEditorOption?.dataSource?.length; i++) {

      }
      return 'test'
    }
    return '';
  }, [selectedInventaire]);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  const renderQuantite = (data: any) => {
    return `${data.data.quantiteModifiee},0000`
  }

  const formatQuantiteEnMain = useCallback((e: { value: unknown }) => {
    if (e.value == null || e.value === '' || Number.isNaN(Number(e.value))) {
      return '0,0000';
    }
    return Number(e.value).toLocaleString('fr-FR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  }, []);

  return (
    <FormListBase data={inventaireFormDataSource}>
      <FormListBaseGrid dataSource={inventaireFormDataSource.dataSource}
        onFocusedRowChanged={() => {
          if (mode === Mode.Modifier) setMode(Mode.Lecture);
        }}
      >
        <Column key={"grid-col-id-produit"} dataField={"idProduit"} caption="Produit" allowHiding={false}>
          <Lookup {...catalogueProduitsEditorOption} />
        </Column>
        <Column key={"grid-col-id-localisation"} dataField={"localisation"} caption="Localisation" allowHiding={false} />
        <Column key={"grid-col-id-quantite"} caption="Quantité" allowHiding={false} cellRender={renderQuantite} />
        <Column
          key={"grid-col-id-date"}
          dataField={"dateModificationInventaire"}
          caption="Date"
          allowHiding={false}
          dataType="date"
          format="yyyy-MM-dd"
        />
        <Column key={"grid-col-id-explication"} dataField={"explication"} caption="Explication" allowHiding={false} />
        <Column key={"grid-col-id-posteEnregistrement"} dataField={"idPosteEnregistrement"} caption="Poste" allowHiding={false}>
          <Lookup {...posteEnregistrementEditorOption} />
        </Column>
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={headerText} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      {/* <FormListBaseToolbar headerText={`${selectedInventaire?.idProduit || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} /> */}
      <FormListBaseTab>
        <PopupGestioCatalogueProduits
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idProduit", data.idProduit);
            setSelectedDataRow((prev) => ({ ...prev, idProduit: data.idProduit }));
            loadInventaireActuel(data.idProduit);
          }}
        >
          <GestionCatalogueProduits isModeSelection={true} />
        </PopupGestioCatalogueProduits>
        <PopupGestionPosteEnregistrement
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idPosteEnregistrement", data.idPosteEnregistrement);
          }}
        >
          <GestionPosteEnregistrement isModeSelection={true} />
        </PopupGestionPosteEnregistrement>
        <PopupGestionLocalisation
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idLocalisation", data.idLocalisation);
          }}
        >
          <GestionLocalisation isModeSelection={true} />
        </PopupGestionLocalisation>
        <TabItem title="Informations">
          <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedInventaire || {} as IModificationInventaire} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={handleFieldDataChanged}>
            <GroupItem colCount={3}>
              <GroupItem colSpan={1}>
                <SimpleItem key={"frm-id-roduit"} dataField={"idProduit"} editorType={"dxSelectBox"} editorOptions={catalogueProduitsEditorOption}                >
                  <Label text={"Produit"} />
                </SimpleItem>
                <SimpleItem key={"frm-quantite-modifiee"} dataField={"quantiteModifiee"} editorType="dxNumberBox" editorOptions={quantiteModifieeEditorOptions}                >
                  <Label text={"Quantité modifiée"} />
                </SimpleItem>
                <SimpleItem key={"frm-date-modification"} dataField={"dateModification"} editorType={"dxDateBox"} editorOptions={dateDeModificationEditorOptions}                >
                  <Label text={"Date de modification"} />
                </SimpleItem>
                <SimpleItem key={"frm-id-poste-enregistrement"} dataField={"idPosteEnregistrement"} editorOptions={posteEnregistrementEditorOption} editorType={"dxSelectBox"}                >
                  <Label text={"Poste"} />
                </SimpleItem>
                <SimpleItem key={"frm-localisation"} dataField={"idLocalisation"} editorType={"dxSelectBox"} editorOptions={localisationEditorOption}                >
                  <Label text={"Localisation"} />
                </SimpleItem>
                <GroupItem caption={"Inventaire actuel"}>
                  <DataGrid
                    showBorders={true}
                    allowColumnResizing={true}
                    dataSource={selectedInventaire?.inventairesActuel ?? []}
                    keyExpr={["idPosteEnregistrement", "emplacement"]}
                    noDataText={"Aucun inventaire"}
                  >
                    <Column dataField={"emplacement"} dataType="string" caption={"Emplacement"} allowEditing={false} />
                    <Column dataField={"quantiteEnMain"} dataType="number" caption={"Quantité en main"} allowEditing={false} customizeText={formatQuantiteEnMain} />
                  </DataGrid>
                </GroupItem>
              </GroupItem>
              <GroupItem colSpan={2}>
                <SimpleItem key={"frm-explication"} dataField={"explication"} editorType={"dxTextArea"} editorOptions={explicationOptions}>
                  <Label text={"Explications"} location={"top"} />
                </SimpleItem>
              </GroupItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedInventaire || {} as IModificationInventaire} /></TabItem>
      </FormListBaseTab >
    </FormListBase >
  );
}